import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, User, Shield, Save, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  authGetRequest,
  authPutRequest,
  getEffectiveClientId,
} from "../../api/authClient";

type MultiSelectOption = { id: string; name: string };

const asObject = (value: any): Record<string, any> =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getNestedPayload = (payload: any) => {
  const root = asObject(payload);
  const data = asObject(root.data);
  const nestedData = asObject(data.data);
  const response = asObject(root.response);
  const nestedResponse = asObject(response.data);
  return { root, data, nestedData, response, nestedResponse };
};

const getPagedList = (payload: any): any[] => {
  const { root, data, nestedData, response, nestedResponse } = getNestedPayload(payload);
  return (
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(nestedData?.items) && nestedData.items) ||
    (Array.isArray(root?.data) && root.data) ||
    (Array.isArray(data?.results) && data.results) ||
    (Array.isArray(nestedData?.results) && nestedData.results) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(nestedData?.data) && nestedData.data) ||
    (Array.isArray(response?.items) && response.items) ||
    (Array.isArray(nestedResponse?.items) && nestedResponse.items) ||
    (Array.isArray(root) && root) ||
    []
  );
};

const getTotalFromPayload = (payload: any): number => {
  const { root, data, nestedData, response, nestedResponse } = getNestedPayload(payload);
  const rawTotal =
    data?.pagination?.total ??
    nestedData?.pagination?.total ??
    response?.pagination?.total ??
    nestedResponse?.pagination?.total ??
    data?.pagination?.totalCount ??
    nestedData?.pagination?.totalCount ??
    data?.meta?.total ??
    nestedData?.meta?.total ??
    data?.total ??
    nestedData?.total ??
    data?.totalCount ??
    nestedData?.totalCount ??
    root?.total ??
    root?.totalCount ??
    0;
  const n = Number(rawTotal);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getPagingMeta = (
  payload: any,
  fallbackPageSize: number,
  fallbackPage: number
): { page: number; size: number; total: number } => {
  const { root, data, nestedData, response, nestedResponse } = getNestedPayload(payload);
  const total = getTotalFromPayload(payload);
  const rawPage =
    data?.pagination?.page ??
    nestedData?.pagination?.page ??
    response?.pagination?.page ??
    nestedResponse?.pagination?.page ??
    data?.meta?.page ??
    nestedData?.meta?.page ??
    data?.page ??
    nestedData?.page ??
    root?.page ??
    fallbackPage;
  const rawSize =
    data?.pagination?.size ??
    nestedData?.pagination?.size ??
    response?.pagination?.size ??
    nestedResponse?.pagination?.size ??
    data?.pagination?.pageSize ??
    nestedData?.pagination?.pageSize ??
    data?.meta?.size ??
    nestedData?.meta?.size ??
    data?.meta?.pageSize ??
    nestedData?.meta?.pageSize ??
    data?.size ??
    nestedData?.size ??
    data?.pageSize ??
    nestedData?.pageSize ??
    root?.size ??
    root?.pageSize ??
    fallbackPageSize;
  const page = Math.max(1, Number(rawPage) || fallbackPage);
  const size = Math.max(1, Number(rawSize) || fallbackPageSize);
  return { page, size, total };
};

function CheckboxMultiSelect({
  id,
  label,
  options,
  selectedValues,
  onChange,
  helperText,
  assignedCount,
  onOpen,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onRetryLoadMore,
  totalCount,
  disabled,
}: {
  id: string;
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (next: string[]) => void;
  helperText?: string;
  assignedCount?: number;
  onOpen?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: string | null;
  onRetryLoadMore?: () => void;
  totalCount?: number;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedLabels = options
    .filter((opt) => selectedValues.includes(opt.id))
    .map((opt) => opt.name);
  const defaultButtonLabel =
    options.length === 0 && (assignedCount ?? 0) > 0
      ? `${assignedCount} assigned`
      : `Select ${label.toLowerCase()}`;
  const extraAssignedCount = Math.max(
    0,
    (assignedCount ?? 0) - selectedLabels.length
  );
  const selectionPreview =
    selectedLabels.length > 0
      ? `${selectedLabels.join(", ")}${
          extraAssignedCount > 0 ? `, +${extraAssignedCount} more...` : ""
        }`
      : defaultButtonLabel;
  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );
  const filteredOptionIds = filteredOptions.map((opt) => opt.id);
  const selectedFilteredCount = filteredOptionIds.filter((id) => selectedValues.includes(id)).length;
  const hasFilteredOptions = filteredOptionIds.length > 0;
  const allFilteredSelected = hasFilteredOptions && selectedFilteredCount === filteredOptionIds.length;
  const someFilteredSelected = selectedFilteredCount > 0 && !allFilteredSelected;
  const normalizedTotalCount =
    typeof totalCount === "number" && totalCount > 0 ? totalCount : options.length;
  const shownCount = searchTerm.trim() ? filteredOptions.length : normalizedTotalCount;
  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    const el = e.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 24) {
      onLoadMore();
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!searchTerm.trim()) return;
    if (!hasMore || isLoadingMore || !onLoadMore) return;
    onLoadMore();
  }, [open, searchTerm, hasMore, isLoadingMore, onLoadMore]);

  const removeSelectedValue = (value: string) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      onChange(selectedValues.filter((value) => !filteredOptionIds.includes(value)));
      return;
    }
    onChange(Array.from(new Set([...selectedValues, ...filteredOptionIds])));
  };

  return (
    <div ref={containerRef}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label}
      </label>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() =>
          setOpen((p) => {
            if (disabled) return p;
            const nextOpen = !p;
            if (nextOpen) onOpen?.();
            return nextOpen;
          })
        }
        className="w-full rounded-lg bg-white border border-slate-300 text-slate-800 px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-1 focus:ring-blue-500/25 focus:border-blue-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[#0B0F19] dark:border-[#334155] dark:text-slate-100"
      >
        <div className="flex items-center justify-between gap-3">
          <span className="truncate">
            {selectionPreview}
          </span>
        </div>
      </button>
      {selectedLabels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {options
            .filter((opt) => selectedValues.includes(opt.id))
            .map((opt) => (
              <span
                key={opt.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:border-[#334155] dark:bg-[#0B0F19] dark:text-slate-300"
              >
                {opt.name}
                <button
                  type="button"
                  onClick={() => removeSelectedValue(opt.id)}
                  className="text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400"
                  aria-label={`Remove ${opt.name}`}
                >
                  x
                </button>
              </span>
            ))}
          {selectedValues.length > 0 ? (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs text-blue-600 dark:text-blue-300 hover:underline"
            >
              Clear all
            </button>
          ) : null}
          {extraAssignedCount > 0 ? (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              +{extraAssignedCount} more assigned...
            </span>
          ) : null}
        </div>
      )}
      {open && (
        <div
          onScroll={handleListScroll}
          className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-300 bg-white dark:bg-[#0B0F19] dark:border-[#334155] px-2 pb-2 pt-0 space-y-2"
        >
          <div className="sticky top-0 z-30 px-0 pt-2 pb-1 bg-white dark:bg-[#0B0F19] border-b border-slate-200 dark:border-[#334155] shadow-sm">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/25 focus:border-blue-500/40 dark:border-[#334155] dark:bg-[#0B0F19] dark:text-slate-200"
            />
            <div className="mt-1 flex items-center justify-between px-0.5">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {shownCount} shown
              </span>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                Assigned: {assignedCount ?? 0}
              </span>
            </div>
            {hasFilteredOptions ? (
              <label className="mt-1 inline-flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someFilteredSelected;
                  }}
                  onChange={toggleSelectAllFiltered}
                  className="h-4 w-4"
                />
                <span>{allFilteredSelected ? "Clear all shown" : "Select all shown"}</span>
              </label>
            ) : null}
          </div>
          {filteredOptions.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-2 py-1">No options found.</p>
          ) : (
            filteredOptions.map((opt) => (
              <label
                key={opt.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-[#1C202F] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.id)}
                  onChange={() => toggleValue(opt.id)}
                  className="h-4 w-4"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">{opt.name}</span>
              </label>
            ))
          )}
          {isLoadingMore ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-2 py-1">Loading more...</p>
          ) : null}
          {searchTerm.trim() && hasMore && !isLoadingMore ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-2 py-1">
              Loading more results for search...
            </p>
          ) : null}
          {loadMoreError ? (
            <div className="flex items-center justify-between gap-2 px-2 py-1">
              <p className="text-xs text-red-500 dark:text-red-400">{loadMoreError}</p>
              {onRetryLoadMore ? (
                <button
                  type="button"
                  onClick={onRetryLoadMore}
                  className="text-xs text-blue-600 dark:text-blue-300 hover:underline"
                >
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {helperText ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p> : null}
    </div>
  );
}

function validateForm(data: Record<string, any>): Record<string, string> {
  const err: Record<string, string> = {};
  if (!data.full_name?.trim()) err.full_name = "Name is required.";
  if (!data.designation?.trim()) err.designation = "Designation is required.";
  if (!data.contact?.trim()) err.contact = "Contact is required.";
  if (!data.company_name?.trim()) err.company_name = "Company name is required.";
  if (!data.department?.trim()) err.department = "Department is required.";
  if (!data.email?.trim()) err.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) err.email = "Invalid email format.";
  return err;
}

export default function EditMember() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [assignedLocationCount, setAssignedLocationCount] = useState(0);
  const [assignedCameraCount, setAssignedCameraCount] = useState(0);
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    company_location: "",
    designation: "",
    email: "",
    contact: "",
    department: "",
    isEmailEnable: false,
    isWhatsappEnable: false,
    viewer_location_details: [] as string[],
    viewer_camera_details: [] as string[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [member, setMember] = useState<any | null>(null);
  const [locationOptions, setLocationOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [cameraOptions, setCameraOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [locationPage, setLocationPage] = useState(1);
  const [cameraPage, setCameraPage] = useState(1);
  const [hasMoreLocations, setHasMoreLocations] = useState(true);
  const [hasMoreCameras, setHasMoreCameras] = useState(true);
  const [locationTotal, setLocationTotal] = useState(0);
  const [cameraTotal, setCameraTotal] = useState(0);
  const [loadingMoreLocations, setLoadingMoreLocations] = useState(false);
  const [loadingMoreCameras, setLoadingMoreCameras] = useState(false);
  const [locationLoadMoreError, setLocationLoadMoreError] = useState<string | null>(null);
  const [cameraLoadMoreError, setCameraLoadMoreError] = useState<string | null>(null);
  const OPTIONS_PAGE_SIZE = 10;
  const [hasLoadedLocationOptions, setHasLoadedLocationOptions] = useState(false);
  const [hasLoadedCameraOptions, setHasLoadedCameraOptions] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const selectedLocationIds = formData.viewer_location_details;
  const isCameraSelectionDisabled = selectedLocationIds.length === 0;
  const selectedLocationsKey = selectedLocationIds.join("|");
  const previousSelectedLocationsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const memberPayload = await authGetRequest(`/client/${id}`);

        const m = memberPayload?.data ?? memberPayload;
        if (!m?.id) {
          setMember(null);
          return;
        }

        setMember(m);
        const backendAllowedLocations = Array.isArray(m.allowed_locations)
          ? m.allowed_locations
          : Array.isArray(m.allowed_location)
          ? m.allowed_location
          : Array.isArray(m.allowedLocationIds)
          ? m.allowedLocationIds
          : Array.isArray(m.allowedLocations)
          ? m.allowedLocations
          : Array.isArray(m.viewer_location_details)
          ? m.viewer_location_details
          : [];
        const backendAllowedCameras = Array.isArray(m.allowed_cameras)
          ? m.allowed_cameras
          : Array.isArray(m.allowed_camera)
          ? m.allowed_camera
          : Array.isArray(m.allowedCameraIds)
          ? m.allowedCameraIds
          : Array.isArray(m.allowedCameras)
          ? m.allowedCameras
          : Array.isArray(m.viewer_camera_details)
          ? m.viewer_camera_details
          : [];
        setAssignedLocationCount(backendAllowedLocations.length);
        setAssignedCameraCount(backendAllowedCameras.length);
        setFormData({
          full_name: m.full_name || "",
          company_name: m.company_name || m.company?.name || "",
          company_location: m.company_location || m.company?.location || "",
          designation: m.designation || "",
          email: m.email || "",
          contact: m.contact || "",
          department: m.department || "",
          isEmailEnable: Boolean(m.isEmailEnable),
          isWhatsappEnable: Boolean(m.isWhatsappEnable),
          viewer_location_details: backendAllowedLocations.map((x: any) => String(x)).filter(Boolean),
          viewer_camera_details: backendAllowedCameras.map((x: any) => String(x)).filter(Boolean),
        });
      } catch (err: any) {
        toast.error(err?.response?.data?.message ?? "Failed to load member details.");
        setMember(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  const loadInitialLocations = useCallback(async () => {
    if (hasLoadedLocationOptions || isLoadingLocations) return;
    setIsLoadingLocations(true);
    setLocationLoadMoreError(null);
    try {
      const currentClientId = getEffectiveClientId() || undefined;
      const payload = await authGetRequest("/location", {
        clientId: currentClientId,
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const locationList = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, 1);
      const mapped = locationList.map((loc: any) => ({
        id: String(loc.id ?? ""),
        name: String(loc.name ?? loc.location_name ?? "Unnamed Location"),
      }));
      setLocationOptions(Array.from(new Map(mapped.map((x) => [x.id, x])).values()));
      setLocationPage(page);
      setLocationTotal(total);
      setHasMoreLocations(mapped.length > 0 && (total > 0 ? total > page * size : mapped.length === size));
      setHasLoadedLocationOptions(true);
    } catch (err: any) {
      setLocationLoadMoreError(err?.response?.data?.message ?? "Failed to load locations.");
    } finally {
      setIsLoadingLocations(false);
    }
  }, [hasLoadedLocationOptions, isLoadingLocations, OPTIONS_PAGE_SIZE]);

  const loadInitialCameras = useCallback(async () => {
    if (hasLoadedCameraOptions || isLoadingCameras) return;
    setIsLoadingCameras(true);
    setCameraLoadMoreError(null);
    try {
      const currentClientId = getEffectiveClientId() || undefined;
      const payload = await authGetRequest("/camera/search", {
        clientId: currentClientId,
        ...(selectedLocationIds.length > 0
          ? { locationIds: selectedLocationIds.join(",") }
          : {}),
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const cameraList = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, 1);
      const mapped = cameraList.map((cam: any) => ({
        id: String(cam.id ?? cam._id ?? ""),
        name: String(cam.aliasName ?? cam.name ?? "Unnamed Camera"),
      }));
      setCameraOptions(Array.from(new Map(mapped.map((x) => [x.id, x])).values()));
      setCameraPage(page);
      setCameraTotal(total);
      setHasMoreCameras(mapped.length > 0 && (total > 0 ? total > page * size : mapped.length === size));
      setHasLoadedCameraOptions(true);
    } catch (err: any) {
      setCameraLoadMoreError(err?.response?.data?.message ?? "Failed to load cameras.");
    } finally {
      setIsLoadingCameras(false);
    }
  }, [hasLoadedCameraOptions, isLoadingCameras, OPTIONS_PAGE_SIZE, selectedLocationIds]);

  const loadMoreLocations = useCallback(async () => {
    if (loadingMoreLocations || !hasMoreLocations || !hasLoadedLocationOptions) return;
    const currentClientId = getEffectiveClientId() || undefined;
    const nextPage = locationPage + 1;
    setLoadingMoreLocations(true);
    setLocationLoadMoreError(null);
    try {
      const payload: any = await authGetRequest("/location", {
        clientId: currentClientId,
        page: nextPage,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const list = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, nextPage);
      const normalizedTotal = total || locationTotal;
      const mapped = list.map((loc: any) => ({
        id: String(loc.id ?? ""),
        name: String(loc.name ?? loc.location_name ?? "Unnamed Location"),
      }));
      setLocationOptions((prev) => {
        const merged = [...prev, ...mapped];
        const unique = Array.from(new Map(merged.map((x) => [x.id, x])).values());
        return unique;
      });
      setLocationPage(page);
      setLocationTotal(normalizedTotal);
      const hasNewItems = mapped.length > 0;
      const canLoadMoreByTotal =
        normalizedTotal > 0 ? normalizedTotal > page * size : mapped.length === size;
      setHasMoreLocations(hasNewItems && canLoadMoreByTotal);
    } catch (err: any) {
      setLocationLoadMoreError(err?.response?.data?.message ?? "Failed to load more locations.");
    } finally {
      setLoadingMoreLocations(false);
    }
  }, [loadingMoreLocations, hasMoreLocations, hasLoadedLocationOptions, locationPage, locationTotal]);

  const loadMoreCameras = useCallback(async () => {
    if (loadingMoreCameras || !hasMoreCameras || !hasLoadedCameraOptions) return;
    const currentClientId = getEffectiveClientId() || undefined;
    const nextPage = cameraPage + 1;
    setLoadingMoreCameras(true);
    setCameraLoadMoreError(null);
    try {
      const payload: any = await authGetRequest("/camera/search", {
        clientId: currentClientId,
        ...(selectedLocationIds.length > 0
          ? { locationIds: selectedLocationIds.join(",") }
          : {}),
        page: nextPage,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const list = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, nextPage);
      const normalizedTotal = total || cameraTotal;
      const mapped = list.map((cam: any) => ({
        id: String(cam.id ?? cam._id ?? ""),
        name: String(cam.aliasName ?? cam.name ?? "Unnamed Camera"),
      }));
      setCameraOptions((prev) => {
        const merged = [...prev, ...mapped];
        const unique = Array.from(new Map(merged.map((x) => [x.id, x])).values());
        return unique;
      });
      setCameraPage(page);
      setCameraTotal(normalizedTotal);
      const hasNewItems = mapped.length > 0;
      const canLoadMoreByTotal =
        normalizedTotal > 0 ? normalizedTotal > page * size : mapped.length === size;
      setHasMoreCameras(hasNewItems && canLoadMoreByTotal);
    } catch (err: any) {
      setCameraLoadMoreError(err?.response?.data?.message ?? "Failed to load more cameras.");
    } finally {
      setLoadingMoreCameras(false);
    }
  }, [
    loadingMoreCameras,
    hasMoreCameras,
    hasLoadedCameraOptions,
    cameraPage,
    cameraTotal,
    selectedLocationIds,
  ]);

  useEffect(() => {
    if (previousSelectedLocationsKeyRef.current === null) {
      previousSelectedLocationsKeyRef.current = selectedLocationsKey;
      return;
    }
    if (previousSelectedLocationsKeyRef.current === selectedLocationsKey) return;
    previousSelectedLocationsKeyRef.current = selectedLocationsKey;
    setCameraOptions([]);
    setCameraPage(1);
    setCameraTotal(0);
    setHasMoreCameras(true);
    setCameraLoadMoreError(null);
    setHasLoadedCameraOptions(false);
    setFormData((prev) => ({ ...prev, viewer_camera_details: [] }));
  }, [selectedLocationsKey]);

  const onChange = (name: string, value: string | string[]) => {
    setFormData((p) => ({ ...p, [name]: value }));
    if (typeof value === "string" && errors[name]) {
      setErrors((p) => ({ ...p, [name]: "" }));
    }
  };

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!id) return;
      const formErrors = validateForm(formData);
      setErrors(formErrors);
      if (Object.keys(formErrors).length > 0) return;

      setIsSubmitting(true);
      try {
        await authPutRequest(`/client/${id}`, {
          full_name: formData.full_name,
          email: formData.email,
          contact: formData.contact,
          designation: formData.designation,
          department: formData.department,
          isEmailEnable: formData.isEmailEnable,
          isWhatsappEnable: formData.isWhatsappEnable,
          company_name: formData.company_name,
          company_location: formData.company_location,
          viewer_location_details: formData.viewer_location_details,
          viewer_camera_details: formData.viewer_camera_details,
        });
        toast.success("Member updated successfully.");
        navigate("/member");
      } catch (err: any) {
        toast.error(err?.response?.data?.message ?? "Failed to update member.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, id, navigate]
  );

  const inputClass =
    "w-full rounded-lg bg-white border border-slate-300 text-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/25 focus:border-blue-500/40 transition-colors dark:bg-[#0B0F19] dark:border-[#334155] dark:text-slate-100 dark:placeholder:text-slate-500";
  const labelClass = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1";

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-slate-600 dark:text-slate-400">Loading member details...</div>
      </div>
    );
  }

  if (!member && id) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-slate-600 dark:text-slate-400">Member not found.</p>
          <button
            onClick={() => navigate("/member")}
            className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
          >
            Back to Members
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-5 min-h-full overflow-y-auto font-sans transition-colors duration-300 bg-white text-slate-900 dark:bg-transparent dark:text-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Edit Member</h2>
          <p className="text-slate-600 dark:text-slate-400">Update member information and permissions</p>
        </div>
        <button
          onClick={() => navigate("/member")}
          className="inline-flex items-center gap-2 px-4 py-2 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#1C202F] border border-slate-300 dark:border-[#334155] hover:bg-slate-100 dark:hover:bg-[#1C202F]/70 rounded-lg font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Members
        </button>
      </div>

      <div className="bg-white/80 dark:bg-[#1C202F]/55 rounded-xl border border-slate-200 dark:border-slate-700/70 overflow-hidden backdrop-blur-sm">
        <form onSubmit={onSubmit} className="p-6 space-y-6">
          <div>
            <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="full_name" className={labelClass}>Full Name*</label>
                <input
                  id="full_name"
                  type="text"
                  placeholder="Enter full name"
                  value={formData.full_name}
                  onChange={(e) => onChange("full_name", e.target.value)}
                  className={`${inputClass} ${errors.full_name ? "border-red-500" : ""}`}
                />
                {errors.full_name && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.full_name}</p>}
              </div>
              <div>
                <label htmlFor="company_name" className={labelClass}>Company Name*</label>
                <input
                  id="company_name"
                  type="text"
                  placeholder="Enter company name"
                  value={formData.company_name}
                  onChange={(e) => onChange("company_name", e.target.value)}
                  className={`${inputClass} ${errors.company_name ? "border-red-500" : ""}`}
                />
                {errors.company_name && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.company_name}</p>}
              </div>
              <div>
                <label htmlFor="company_location" className={labelClass}>Company Location</label>
                <input
                  id="company_location"
                  type="text"
                  placeholder="Enter company location"
                  value={formData.company_location}
                  onChange={(e) => onChange("company_location", e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="department" className={labelClass}>Department*</label>
                <input
                  id="department"
                  type="text"
                  placeholder="Enter department"
                  value={formData.department}
                  onChange={(e) => onChange("department", e.target.value)}
                  className={`${inputClass} ${errors.department ? "border-red-500" : ""}`}
                />
                {errors.department && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.department}</p>}
              </div>
              <div>
                <label htmlFor="designation" className={labelClass}>Designation*</label>
                <input
                  id="designation"
                  type="text"
                  placeholder="Enter designation"
                  value={formData.designation}
                  onChange={(e) => onChange("designation", e.target.value)}
                  className={`${inputClass} ${errors.designation ? "border-red-500" : ""}`}
                />
                {errors.designation && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.designation}</p>}
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email Address*</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  className={`${inputClass} ${errors.email ? "border-red-500" : ""}`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="contact" className={labelClass}>Contact Number*</label>
                <input
                  id="contact"
                  type="text"
                  placeholder="Enter contact number"
                  value={formData.contact}
                  onChange={(e) => onChange("contact", e.target.value)}
                  className={`${inputClass} ${errors.contact ? "border-red-500" : ""}`}
                />
                {errors.contact && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.contact}</p>}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              Notification Alerts
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-blue-500 focus:ring-blue-500"
                  checked={formData.isEmailEnable}
                  onChange={(e) => onChange("isEmailEnable", e.target.checked as any)}
                />
                <span>Email alerts</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-blue-500 focus:ring-blue-500"
                  checked={formData.isWhatsappEnable}
                  onChange={(e) => onChange("isWhatsappEnable", e.target.checked as any)}
                />
                <span>WhatsApp alerts</span>
              </label>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              Assign Locations & Cameras
            </h4>
            <div className="grid grid-cols-1 space-y-4">
              <div>
                <CheckboxMultiSelect
                  id="viewer_location_details"
                  label="Allowed Locations"
                  options={locationOptions}
                  selectedValues={formData.viewer_location_details}
                  onChange={(next) => onChange("viewer_location_details", next)}
                  assignedCount={assignedLocationCount}
                  onOpen={loadInitialLocations}
                  onLoadMore={loadMoreLocations}
                  hasMore={hasMoreLocations}
                  isLoadingMore={loadingMoreLocations || isLoadingLocations}
                  loadMoreError={locationLoadMoreError}
                  onRetryLoadMore={hasLoadedLocationOptions ? loadMoreLocations : loadInitialLocations}
                  totalCount={locationTotal}
                  helperText="Choose one or more locations for this member."
                />
              </div>
              <div className="md:col-span-2">
                <CheckboxMultiSelect
                  id="viewer_camera_details"
                  label="Allowed Cameras"
                  options={cameraOptions}
                  selectedValues={formData.viewer_camera_details}
                  onChange={(next) => onChange("viewer_camera_details", next)}
                  assignedCount={assignedCameraCount}
                  onOpen={loadInitialCameras}
                  onLoadMore={loadMoreCameras}
                  hasMore={hasMoreCameras}
                  isLoadingMore={loadingMoreCameras || isLoadingCameras}
                  loadMoreError={cameraLoadMoreError}
                  onRetryLoadMore={hasLoadedCameraOptions ? loadMoreCameras : loadInitialCameras}
                  totalCount={cameraTotal}
                  disabled={isCameraSelectionDisabled}
                  helperText={
                    isCameraSelectionDisabled
                      ? "Select at least one location to choose cameras."
                      : "Selected member should only access these cameras."
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-slate-700">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
